import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { useSite, usePublic } from "@/context/SiteContext";
import { assetUrl, money } from "@/lib/api";
import { PageHero, SectionHead, LightSection } from "@/components/site/Shared";
import { CampaignProgress } from "@/components/site/CampaignProgress";

const COLORS = ["bg-tang", "bg-aqua", "bg-mustard", "bg-quartz", "bg-lav", "bg-passion"];

const Team = ({ s }) => {
  const people = usePublic("team") || [];
  if (!people.length) return null;
  return (
    <section className="container-mas py-16 sm:py-20" data-testid="impact-team">
      <SectionHead eyebrow="who your gift supports" title={s.team_heading} intro={s.team_intro} className="mb-10" />
      <div className="grid md:grid-cols-2 gap-6">
        {people.map((p, i) => (
          <article key={p.id} className="bg-shell text-ink grid sm:grid-cols-[200px_1fr]" data-testid={`team-member-${p.id}`}>
            <div className="relative aspect-square sm:aspect-auto sm:h-full min-h-[200px] overflow-hidden bg-ink">
              {p.image && <img src={assetUrl(p.image)} alt={p.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />}
              <span className={`absolute left-0 bottom-0 ${COLORS[i % COLORS.length]} w-full h-2`} />
            </div>
            <div className="p-6 flex flex-col">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-passion" data-testid={`team-title-${p.id}`}>{p.title}</p>
              <h3 className="text-2xl font-semibold mt-2">{p.name}</h3>
              <p className="text-sm leading-relaxed mt-3 flex-1 whitespace-pre-line">{p.description}</p>
              {p.email && <a href={`mailto:${p.email}`} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold hover:text-passion break-all" data-testid={`team-email-${p.id}`}><Mail className="w-4 h-4 shrink-0" />{p.email}</a>}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default function Impact() {
  const { settings: s } = useSite() || {};
  if (!s) return <div className="h-[80vh]" />;
  const items = s.impact_items || [];
  return (
    <div data-testid="impact-page">
      <PageHero testid="impact-hero" eyebrow="impact" title="Where every dollar goes" intro={s.impact_intro} image={s.images?.impact}>
        <Link to="/donate" className="btn-terra" data-testid="impact-donate-button">Fund the next {s.campaign_years} years</Link>
      </PageHero>
      <section className="container-mas py-16 sm:py-20 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <SectionHead eyebrow="fund allocation" title={`${money(s.campaign_goal)} over ${s.campaign_years} years`} intro={`Sustaining ${s.events_per_year}+ free poetry events and workshops every year.`} />
          <div className="flex h-8 mt-8 overflow-hidden" data-testid="impact-allocation-bar">
            {items.map((it, i) => <div key={i} className={COLORS[i % COLORS.length]} style={{ width: `${it.percent}%` }} title={it.label} />)}
          </div>
        </div>
        <ol className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
          {items.map((it, i) => (
            <li key={i} className={`${COLORS[i % COLORS.length]} text-ink p-6 min-h-[200px] flex flex-col`} data-testid={`impact-item-${i}`}>
              <p className="font-display text-5xl leading-none">{it.percent}%</p>
              <h3 className="text-xl font-semibold mt-auto pt-6">{it.label}</h3>
              <p className="text-sm mt-1 opacity-80">{it.description}</p>
              <p className="font-mono text-xs mt-2">≈ {money((s.campaign_goal * it.percent) / 100)} of the goal</p>
            </li>
          ))}
        </ol>
      </section>
      <Team s={s} />
      <LightSection>
        <div className="container-mas py-16 sm:py-20">
          <SectionHead light eyebrow="what contributions make possible" title="Your gift, translated" className="mb-10" />
          <ol className="divide-y divide-mas-sand border-y-2 border-ink">
            {(s.contribution_levels || []).map((c, i) => (
              <li key={i}>
                <Link to="/donate" className="group flex items-baseline gap-6 py-5 hover:bg-white transition-colors px-2" data-testid={`impact-level-${i}`}>
                  <span className="font-mono text-passion w-5">{i + 1}</span>
                  <span className="font-display text-3xl sm:text-4xl w-32 sm:w-40 shrink-0">{money(c.amount)}</span>
                  <span className="text-base sm:text-lg">{c.outcome}</span>
                </Link>
              </li>
            ))}
          </ol>
        </div>
      </LightSection>
      <section className="container-mas py-16 sm:py-20 max-w-3xl"><CampaignProgress variant="dark" testid="impact-campaign-progress" /></section>
    </div>
  );
}
